"use client";

import React, { FormEvent } from "react";
import Image from "next/image";
import {
  OakBox,
  OakFlex,
  OakInlineBanner,
  OakPrimaryButton,
} from "@oaknational/oak-components";

import { useGoogleClassroomAddonStore } from "@/store";
import type { NewAttachment } from "@/types/ui";
import { OakAcornIconWithText } from "@/components";

export type OnLessonAttachedData = {
  lessonName: string;
  unitName: string;
  courseId: string;
  itemId: string;
  gradeSyncEnabled: boolean;
  googleLoginHint?: string;
};

type Props = {
  children: React.ReactNode;
  createAttachmentAction: (attachment: NewAttachment) => Promise<void>;
  onLessonAttached?: (data: OnLessonAttachedData) => Promise<void> | void;
};
export const BrowseLayout = ({
  children,
  createAttachmentAction,
  onLessonAttached,
}: Props) => {
  const formId = "lesson-selection-form";
  const Lessons = useGoogleClassroomAddonStore((state) => state.lessons);
  const userProfilePicUrl = useGoogleClassroomAddonStore(
    (state) => state.userProfilePicUrl,
  );
  const courseId = useGoogleClassroomAddonStore((state) => state.courseId);
  const itemId = useGoogleClassroomAddonStore((state) => state.itemId);
  const addOnToken = useGoogleClassroomAddonStore((state) => state.addOnToken);
  const lessonSelected = useGoogleClassroomAddonStore(
    (state) => state.lessonSelected,
  );
  const isGradeSynced = useGoogleClassroomAddonStore(
    (state) => state.isGradeSynced,
  );
  const googleLoginHint = useGoogleClassroomAddonStore(
    (state) => state.googleLoginHint,
  );
  const [attachLessonsLoading, setAttachLessonsLoading] = React.useState(false);

  const submitAttachLessons = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    setAttachLessonsLoading(true);
    const [entry] = formData.entries();
    const lessonSelectionSlug = entry?.[1];
    const selectedLesson = Lessons.find(
      (l) => l.lessonSlug === lessonSelectionSlug,
    );
    try {
      await onLessonAttached?.({
        lessonName: selectedLesson?.title ?? "",
        unitName: selectedLesson?.unitTitle ?? "",
        courseId: courseId ?? "",
        itemId: itemId ?? "",
        gradeSyncEnabled: isGradeSynced ?? false,
        googleLoginHint,
      });
      await createAttachmentAction({
        courseId: courseId ?? "",
        itemId: itemId ?? "",
        addOnToken: addOnToken ?? "",
        title: selectedLesson?.title ?? "",
        lessonSlug: selectedLesson?.lessonSlug ?? "",
        programmeSlug: selectedLesson?.programmeSlug ?? "",
        unitSlug: selectedLesson?.unitSlug ?? "",
        maxPoints: isGradeSynced ? selectedLesson?.maxPoints : undefined,
      });
    } finally {
      setAttachLessonsLoading(false);
    }
    // close iframe
    window?.parent?.postMessage(
      {
        type: "Classroom",
        action: "closeIframe",
      },
      "*",
    );
  };
  return (
    <OakBox as="form" id={formId} onSubmit={submitAttachLessons}>
      <OakFlex
        $alignItems={"center"}
        $justifyContent={"space-between"}
        $pv={"spacing-12"}
        $ph={"spacing-32"}
        $background={"white"}
        $width={"100%"}
        $position={"sticky"}
        $top={"spacing-0"}
        $bb={"border-solid-s"}
        $borderColor={"border-neutral-lighter"}
        $zIndex={"fixed-header"}
      >
        <OakAcornIconWithText />
        <OakFlex
          $borderRadius={"border-radius-circle"}
          $width={"spacing-40"}
          $height={"spacing-40"}
          $overflow={"hidden"}
          $alignItems={"center"}
          $justifyContent={"center"}
          $position={"relative"}
        >
          {userProfilePicUrl && (
            <Image
              src={userProfilePicUrl}
              alt={"User Avatar"}
              fill
              objectFit={"cover"}
            />
          )}
        </OakFlex>
      </OakFlex>
      {children}
      {lessonSelected && Lessons.length > 0 && (
        <OakFlex
          $justifyContent={isGradeSynced ? "space-between" : "flex-end"}
          $flexWrap={"wrap"}
          $pa={"spacing-16"}
          $background={"white"}
          $bt={"border-solid-s"}
          $position={"sticky"}
          $bottom={"spacing-0"}
          $width={"100%"}
          $zIndex={"fixed-header"}
          $gap={"spacing-16"}
        >
          {isGradeSynced && (
            <OakInlineBanner
              isOpen
              message="The assignment will use the Exit quiz score from the selected lesson for marking."
              titleTag="h2"
              type="alert"
              variant="regular"
            />
          )}
          <OakFlex $alignItems={"center"} $gap={"spacing-16"}>
            <OakPrimaryButton
              type="submit"
              form={formId}
              iconName={"google-classroom"}
              isTrailingIcon={true}
              isLoading={attachLessonsLoading}
            >
              Attach lesson to assignment
            </OakPrimaryButton>
          </OakFlex>
        </OakFlex>
      )}
    </OakBox>
  );
};
